import { SearchParameters } from 'algoliasearch-helper'
import { Axios } from 'cms-api'
import { type GetStaticProps, type NextPageWithLayout } from 'next'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useState } from 'react'
import { InstantSearch } from 'react-instantsearch'
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter'
import { getPublicLayout } from '@public/components/PublicLayout'
import {
  EXCLUDE_CUSTOM_SALES_FILTER,
  ShopPageLayout,
} from 'components/InstantSearch'
import { BRAND_PAGE_SORT_OPTIONS } from 'components/InstantSearch/TypeSenseSortBy'
import { Seo } from 'components/Seo'
import { prepareGetStaticNavigationProps } from 'routes/utils/prepareGetStaticNavigationProps'
import { getTypesenseConfigurationOptions } from 'utils/typesenseClient'

const { searchClient } = new TypesenseInstantSearchAdapter({
  server: getTypesenseConfigurationOptions(),
  additionalSearchParameters: {
    query_by: 'title,embedding',
    include_fields:
      'id,title,imageUrl,imageAlt,minPrice,maxPrice,minTradePrice,maxTradePrice,minTradeDiscount,legacyProductId,breakoutOptionCount,carouselVariants,legacyProductVariantId,brand,availabilityOrder, itemTypes',
    sort_by: BRAND_PAGE_SORT_OPTIONS[0].value.replace('products-v5/sort/', ''),
  },
})

export const BlackFridayPage: NextPageWithLayout = ({
  includedBrands,
}: {
  includedBrands: string[]
}) => {
  const router = useRouter()
  const [routingKey, setRoutingKey] = useState(0)

  // next link has problem with routing when navigate to the same page with different query params
  useEffect(() => {
    setRoutingKey((k) => k + 1)
  }, [router.asPath])

  const searchParameters = useMemo(() => {
    const brandFilter =
      includedBrands && includedBrands.length > 0
        ? ` && brand:=[${includedBrands.map((b) => `\`${b}\``).join(',')}]`
        : ''
    return new SearchParameters({
      hitsPerPage: 32,
      filters: `status:=PUBLISHED && ${EXCLUDE_CUSTOM_SALES_FILTER} ${brandFilter}`,
    })
  }, [includedBrands])

  return (
    <>
      <Seo
        title="The Expert - Shop Designer Curated Home Decor"
        description="Get the look of your favorite interior designer by shopping the Showrooms of Experts from Jake Arnold to Jenna Lyons. Discover vintage and new furniture, lighting, rugs, and more."
      />
      <InstantSearch
        key={routingKey}
        searchClient={searchClient}
        indexName="products-v5"
        routing
      >
        <ShopPageLayout
          searchParameters={searchParameters}
          showParentLevel={false}
        />
      </InstantSearch>
    </>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  const baseProps = await prepareGetStaticNavigationProps()

  const promoCodeResponse = await Axios.client().getPromoCodes({
    filters: { code: 'BFCM' },
    populate: 'includedBrands',
    'pagination[limit]': 500,
  })

  const promoCode = promoCodeResponse?.data?.[0]

  const includedBrands = promoCode
    ? promoCode?.includedBrands.map(
        (includedBrand) => (includedBrand as { name: string }).name
      ) || []
    : []

  return {
    props: {
      ...baseProps.props,
      breadcrumbs: [
        { label: 'Shop', link: '/shop' },
        {
          label: 'Black Friday Sale',
          link: '/shop/black-friday',
          disabled: true,
        },
      ],
      includedBrands,
    },
    revalidate: 24 * 3600,
    notFound: baseProps.notFound,
  }
}

BlackFridayPage.getLayout = (page) =>
  getPublicLayout(page, { isWithBreadcrumbs: true, isFullWidth: true })
